// src/lib/weldingWords.js
// The Welding page's sentences (#746), Session 79.22b.6.3 - gate 746's copy
// table (section 7, F2), word for word. Pure, so the board, the size's sheet
// (79.22b.6.3.1) and the desk (79.22b.6.4) say every number the same way, and
// weldingBoard746.test.js can hold each one to the table.

// "7:40 am" - the copy table's own form, lower case.
export function clockTime(at) {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
}

// "Oct 6" from the device's YYYY-MM-DD.
export function shortDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Under the title (746 D32): "Houston · Oct 6 · weld-by dates, late first", or
// "… · late first" with Days to coat and ship at 0.
export function boardSubtitle({ branchName, today, coatShipDays }) {
  const order = Number(coatShipDays) > 0 ? 'weld-by dates, late first' : 'late first';
  return [branchName, shortDay(today), order].filter(Boolean).join(' · ');
}

// "Next 7 days", and with a look-ahead of 1, "Tomorrow".
export function nextLabel(horizonDays) {
  return Number(horizonDays) === 1 ? 'Tomorrow' : `Next ${horizonDays} days`;
}

// A bin's due line (746 D32): Late · Today · Next {N} days · No date · Shelf
// top-up · Reserve, each only when above 0, in that order. '' when none is.
export function dueLine(size, horizonDays) {
  return [
    ['Late', size.late], ['Today', size.today], [nextLabel(horizonDays), size.next],
    ['No date', size.noDate], ['Shelf top-up', size.shelfTopUp], ['Reserve', size.reserve],
  ].filter(([, n]) => Number(n) > 0).map(([label, n]) => `${label} ${n}`).join(' · ');
}

// The shelf's second line: "8 on the shelf · reorder point 20", and at the
// point (shortfall 0), "20 on the shelf · at its reorder point of 20" (#758).
export function shelfLine(shelf) {
  if (!shelf) return '';
  const onHand = Number(shelf.qtyOnHand) || 0;
  return shelf.qty > 0
    ? `${onHand} on the shelf · reorder point ${shelf.reorderPoint}`
    : `${onHand} on the shelf · at its reorder point of ${shelf.reorderPoint}`;
}

// "Later 60, not counted" - on its own line, never in the bar.
export function laterLine(later) {
  return Number(later) > 0 ? `Later ${later}, not counted` : '';
}

// How full a bin's bar is: what is welded and waiting against everything
// counted, as a whole percent. A Covered size is full.
export function fillPercent(size) {
  if (size.state === 'covered') return 100;
  if (!(size.counted > 0)) return 0;
  return Math.min(100, Math.round((Math.max(size.waitingShown, 0) / size.counted) * 100));
}

// Who is on a size (746 D2, D15), from flagsShown()'s list, oldest first.
// Somebody else's: "Owen Pike is on it · since 7:40 am" · "Owen Pike and Tess
// Ivory are on it" · "Owen Pike and 2 others are on it". Your own, with Stop:
// "You're on this · since 10:12 am" · "You and Owen Pike are on this".
export function flagWords(flags, myId) {
  const list = Array.isArray(flags) ? flags : [];
  if (!list.length) return null;
  const mine = list.find(f => myId != null && String(f.user_id) === String(myId)) || null;
  const others = list.filter(f => f !== mine).map(f => f.user_name || 'Someone');
  if (mine) {
    if (!others.length) return { mine: true, text: `You're on this · since ${clockTime(mine.started_at)}` };
    if (others.length === 1) return { mine: true, text: `You and ${others[0]} are on this` };
    return { mine: true, text: `You and ${others.length} others are on this` };
  }
  if (others.length === 1) return { mine: false, text: `${others[0]} is on it · since ${clockTime(list[0].started_at)}` };
  if (others.length === 2) return { mine: false, text: `${others[0]} and ${others[1]} are on it` };
  return { mine: false, text: `${others[0]} and ${others.length - 1} others are on it` };
}

// The board's whole-list sentences (746 D33).
export const NOTHING_TO_WELD = 'Nothing to weld. No orders are open, no shelf is low, and no size has a reserve.';
export function allCoveredLine(horizonDays, anyReserve) {
  const one = Number(horizonDays) === 1;
  if (!anyReserve) {
    return one ? 'Welded frames and stakes cover today and tomorrow.'
      : `Welded frames and stakes cover the next ${horizonDays} days.`;
  }
  return one ? 'Welded frames and stakes cover today, tomorrow and every reserve.'
    : `Welded frames and stakes cover the next ${horizonDays} days and every reserve.`;
}

// #791, Colby's rulings at 79.22b.6.3 (R1 D3) and 79.22b.6.3.1 (R1 D2): the
// count is "Recount welded frames" everywhere the page names it, the tap hint
// included - the button alone renamed would leave the board telling a welder to
// do something no button says.
export const TAP_HINT = 'Tap a size to record, flag it, or recount welded frames.';
export const LOADING_LINE = 'Loading the welding list.';
export const COULD_NOT_LOAD = 'The welding list could not be loaded.';

// ─────────────────────────────────────────────────────────────────────────────
// A SIZE'S SHEET (F2-F4) - Session 79.22b.6.3.1
// ─────────────────────────────────────────────────────────────────────────────

export const RECOUNT_LABEL = 'Recount welded frames';
export const NOBODY_ON_IT = 'No one is on it.';
export const NOTHING_TODAY = 'Nothing has been recorded for this size today.';
export const TYPE_FIRST = 'Type how many first.';
export const RECORDING = 'Recording…';
export const UNDOING = 'Undoing…';
export const NOT_SAVED = "Stede couldn't save that. Nothing was changed. Try again.";

// "Weld-by dates: 7 days before each order is due, to coat and ship." -
// "1 day" at 1, and no line at all at 0 (746 D32).
export function weldByLine(coatShipDays) {
  const s = Number(coatShipDays);
  if (!(s > 0)) return '';
  return `Weld-by dates: ${s} ${s === 1 ? 'day' : 'days'} before each order is due, to coat and ship.`;
}

// "10 welded, waiting · 50 still to weld"
export function countsLine(size) {
  return `${size.waitingShown} welded, waiting · ${size.stillToWeld} still to weld`;
}

// The reserve line (746 D30, D32): "Reserve 20 · set by Nina Hart · Oct 3", or
// "Reserve 0" for a size nobody has set. Change is 79.22b.6.4b's.
export function reserveLine(size) {
  const n = Number(size.reserve) || 0;
  if (!size.reserveSetBy) return `Reserve ${n}`;
  const at = size.reserveSetAt ? new Date(size.reserveSetAt) : null;
  const day = at && !Number.isNaN(at.getTime())
    ? at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return [`Reserve ${n}`, `set by ${size.reserveSetBy}`, day].filter(Boolean).join(' · ');
}

// The preview under the number (F2, 746 D33). `qty` is the box's string.
export function previewLine(size, qty) {
  const n = parseInt(String(qty ?? ''), 10);
  if (!(n > 0)) return 'Type how many, then record.';
  const after = Math.max((Number(size.weldedWaiting) || 0) + n, 0);
  const counted = Number(size.counted) || 0;
  if (after <= counted) return `This puts welded, waiting at ${after} and still to weld at ${counted - after}.`;
  const more = after - counted;
  return Number(size.reserve) > 0
    ? `This puts welded, waiting at ${after}. That covers everything due, the reserve, and ${more} more.`
    : `This puts welded, waiting at ${after}. That covers everything due and ${more} more.`;
}

// "Record welded" with no number yet, "Record 10 welded" with one.
export function recordLabel(qty) {
  const n = parseInt(String(qty ?? ''), 10);
  return n > 0 ? `Record ${n} welded` : 'Record welded';
}

// When you are on another size, said before the press so there is no dialog.
export function movedLine(otherName, helping) {
  return helping
    ? `You're on ${otherName}. Helping here moves you.`
    : `You're on ${otherName}. This moves you.`;
}

// Why this person may not record here, in the order the database refuses
// (permissions.js, weldRefusalKind()). #791's ruling reaches the team member's
// sentence (R1 D2).
export function refusalReason(kind, branchName) {
  switch (kind) {
    case 'team':
      return "Ask an admin to turn on Can record welds for you. You need it to record welds, to recount welded frames, and to say you're on it.";
    case 'manager':
      return "Ask an admin to turn on Can record welds for you. You need it to record welds and to say you're on it.";
    case 'purchasing':
      return "Purchasing can see the welding list but doesn't record welds.";
    case 'away':
      return `Only someone at ${branchName || 'this branch'} can record welds.`;
    default:
      return null;
  }
}

// A refusal from one of the button calls, in the gate's words. The welding
// steps refuse with the copy table's own sentences under these codes; anything
// else - a dropped connection, an unexpected error - is the copy table's
// "anything else" sentence, never a technical message.
const REFUSAL_CODES = new Set(['22023', '23505', '42501', '55000', 'P0001']);
export function refusalWords(error) {
  if (!error) return '';
  return REFUSAL_CODES.has(String(error.code || '')) && error.message ? error.message : NOT_SAVED;
}

// Today's entries (746 D20), newest first: "12 welded · Owen Pike · 9:20 am",
// "Undone: 12 welded · Owen Pike · 9:21 am", "Counted 0 · Owen Pike · 6:55 am".
export function todayEntryLine(entry) {
  const who = entry.byName || 'Someone';
  const at = clockTime(entry.at);
  if (entry.kind === 'count') return `Counted ${entry.qty} · ${who} · ${at}`;
  if (entry.kind === 'undo') return `Undone: ${Math.abs(Number(entry.qty) || 0)} welded · ${who} · ${at}`;
  return `${entry.qty} welded · ${who} · ${at}`;
}

// The answer bar (F3): after recording, with Undo; after an Undo, without.
export function recordedAnswer(qty, sizeName) {
  return `Recorded ${qty} × ${sizeName}.`;
}
export function undoneAnswer(weldedWaiting) {
  return `Undone. Welded, waiting is back to ${Math.max(Number(weldedWaiting) || 0, 0)}.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// THE DESK (F6), CLEAR FLAG AND THE BRANCH CHIP (S8) - Session 79.22b.6.4
// ─────────────────────────────────────────────────────────────────────────────

export const NEXT_UP = 'Next up';

// The desk's summary line (746 D32): "All sizes · Late 38 · Today 79 · Next 7
// days 143 · No date 6 · Later 200, not counted · Shelf top-up 12 · Reserve 66 ·
// Welded, waiting 85" - every size the table lists, each part only above 0, as
// the due line's rule (the amended F6 draws no No date, having none).
export function summaryLine(sizes, horizonDays) {
  const list = Array.isArray(sizes) ? sizes : [];
  const add = key => list.reduce((n, s) => n + (Number(s[key]) || 0), 0);
  const parts = [
    ['Late', add('late')], ['Today', add('today')], [nextLabel(horizonDays), add('next')],
    ['No date', add('noDate')],
  ].filter(([, n]) => n > 0).map(([label, n]) => `${label} ${n}`);
  const later = add('later');
  if (later > 0) parts.push(`Later ${later}, not counted`);
  for (const [label, n] of [['Shelf top-up', add('shelfTopUp')], ['Reserve', add('reserve')], ['Welded, waiting', add('waitingShown')]]) {
    if (n > 0) parts.push(`${label} ${n}`);
  }
  return ['All sizes', ...parts].join(' · ');
}

// A desk row's lines (the copy table's "Desk: row lines"): "Owen Pike · since
// 7:40 am", "8 on the shelf, reorder point 20", "Later 30, not counted".
export function deskWhoLine(flag) {
  return `${flag?.user_name || 'Someone'} · since ${clockTime(flag?.started_at)}`;
}
export function deskOwnLine(flag) {
  return `You're on this · since ${clockTime(flag?.started_at)}`;
}
export function deskShelfLine(shelf) {
  if (!shelf) return '';
  const onHand = Number(shelf.qtyOnHand) || 0;
  return shelf.qty > 0
    ? `${onHand} on the shelf, reorder point ${shelf.reorderPoint}`
    : `${onHand} on the shelf, at its reorder point of ${shelf.reorderPoint}`;
}

// Clear flag (746 D15; the copy table): the answer names whose flag it was.
export function clearedAnswer(name) {
  return `Cleared ${name || 'Someone'}'s flag.`;
}
export const FLAG_ALREADY_ENDED = 'That flag had already ended.';

// The branch chip (746 D23), admins and executives only.
export const BRANCH_HINT = 'Tap to switch branch';

// ─────────────────────────────────────────────────────────────────────────────
// THE STATES AND THE COUNT SHEET (S1-S7) - Session 79.22b.6.4.1
// ─────────────────────────────────────────────────────────────────────────────

// S2's table row: a read that came back cut at the database's 1,000 rows says
// why, instead of "could not be loaded".
export const CUT_READ = 'There are more open orders than Stede can load at once, so this list would be wrong. Tell an admin.';

// A bin's first line (the gate's F1 state table): the due line, else "Nothing
// on order" - but never for a size whose orders are all beyond the look-ahead,
// which has orders and shows only "Later 50, not counted" (edge: only later).
export const NOTHING_ON_ORDER = 'Nothing on order';
export function binFirstLine(size, horizonDays) {
  const due = dueLine(size, horizonDays);
  if (due) return due;
  return Number(size.later) > 0 ? '' : NOTHING_ON_ORDER;
}

// S7, in #791's words (R1 D2 at 6.3.1: the note follows the button's new name).
export function coatedNote(size) {
  const over = Math.max(-(Number(size.weldedWaiting) || 0), 0);
  return `Powder coating signed off ${over} more than were recorded as welded. Recount welded frames.`;
}

// S6, the count sheet - the copy table's "Count sheet" row, with #791's helper
// line under the question (6.3's R1 D3).
export const COUNT_ASK = 'How many are welded and waiting right now?';
export const COUNT_HELP = 'Count them at powder coating. Your number replaces the one Stede has.';
export const SAVING = 'Saving…';
export function wasLine(size) {
  return `Was ${Math.max(Number(size.waitingShown) || 0, 0)}.`;
}
// A whole number of frames, 0 included: a count of none is a real count.
function countOf(qty) {
  const s = String(qty ?? '').trim();
  return /^\d+$/.test(s) ? Number(s) : null;
}
export function countPreview(size, qty) {
  const n = countOf(qty);
  if (n == null) return 'Type how many, then set it.';
  const left = Math.max((Number(size.counted) || 0) - n, 0);
  return `This sets welded, waiting to ${n} and still to weld to ${left}.`;
}
export function countLabel(qty) {
  const n = countOf(qty);
  return n == null ? 'Set it' : `Set it to ${n}`;
}
export function countedAnswer(n) {
  return `Welded, waiting set to ${n}. Count again to change it.`;
}
export { countOf };

// A branch that welds nothing (S8): "Nothing is welded at Dallas, because
// Houston welds the frames and stakes." · two: "…because Houston and Austin weld
// the frames and stakes." · none: "No branch welds anything yet."
export function nothingWeldedAt(branchName, weldingNames) {
  const names = (Array.isArray(weldingNames) ? weldingNames : []).filter(Boolean);
  if (!names.length) return 'No branch welds anything yet.';
  const who = names.length === 1 ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Nothing is welded at ${branchName || 'this branch'}, because ${who} ${names.length === 1 ? 'welds' : 'weld'} the frames and stakes.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHANGING A RESERVE (R1-R4; 746 D29-D31) - Session 79.22b.6.4b
// ─────────────────────────────────────────────────────────────────────────────

// The board's button, and the sheet's reserve line's (only for those allowed).
export const RESERVES_LABEL = 'Reserves';
export const CHANGE_LABEL = 'Change';
export const NOTHING_CHANGED = 'Nothing has changed.';
export const CANCEL_LABEL = 'Cancel';

// R2 - one size: "Reserve · Frame Std 24x36", the help, "Was 20.", the preview
// "With a reserve of 30, still to weld becomes 60.", "Set reserve to 30", and
// after it "Reserve for Frame Std 24x36 set to 30." (no Undo).
export function reserveTitle(sizeName) {
  return `Reserve · ${sizeName}`;
}
export const RESERVE_HELP = 'How many to keep welded and waiting at powder coating.';
export function reserveWasLine(n) {
  return `Was ${Math.max(Number(n) || 0, 0)}.`;
}
export function reservePreview(reserve, stillToWeld) {
  return `With a reserve of ${reserve}, still to weld becomes ${stillToWeld}.`;
}
export function setReserveLabel(qty) {
  const s = String(qty ?? '').trim();
  return /^\d+$/.test(s) ? `Set reserve to ${Number(s)}` : 'Set reserve';
}
export function reserveSetAnswer(sizeName, n) {
  return `Reserve for ${sizeName} set to ${n}.`;
}

// R3, R4 - every size: "Reserves · Houston", the help, "Change every size by
// [ ] %", Apply, "Rounded to whole frames.", "was 20" under a changed size,
// "Save 4 changes" (one: "Save 1 change"; none: "Save"), and after it
// "Reserves saved for 4 sizes." (one: "…for 1 size.").
export function reservesTitle(branchName) {
  return branchName ? `Reserves · ${branchName}` : 'Reserves';
}
export const RESERVES_HELP = 'How many welded frames and stakes to keep waiting at powder coating, size by size.';
export const FILL_LABEL = 'Change every size by';
export const APPLY_LABEL = 'Apply';
export const ROUNDED_NOTE = 'Rounded to whole frames.';
export const TYPE_PERCENT = 'Type a percent first.';
export function reserveWasShort(n) {
  return `was ${Math.max(Number(n) || 0, 0)}`;
}
export function saveChangesLabel(count) {
  const n = Number(count) || 0;
  if (n === 0) return 'Save';
  return n === 1 ? 'Save 1 change' : `Save ${n} changes`;
}
export function reservesSavedAnswer(count) {
  const n = Number(count) || 0;
  return n === 1 ? 'Reserves saved for 1 size.' : `Reserves saved for ${n} sizes.`;
}
